import type { DashboardStats } from '@/shared/api/types/dashboard-types'
import { BarChart2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { cn } from '@/shared/libs/cn'
import { Skeleton } from '@/shared/ui/skeleton'

// Chart display constants
const CHART_HEIGHT = 240
const CHART_BAR_COLOR = '#2563EB'
const CHART_BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0]
const CHART_BAR_MAX_SIZE = 60
const CHART_CATEGORY_GAP = '30%'
const CHART_AXIS_FONT_SIZE = 11
const CHART_AXIS_COLOR = '#71717A'
const CHART_LABEL_MAX_LENGTH = 14
const CHART_TOOLTIP_STYLE = {
  border: '1px solid #E4E4E7',
  borderRadius: 8,
  fontSize: 12,
  boxShadow: 'none',
}

function truncateLabel(value: string, maxLength = CHART_LABEL_MAX_LENGTH) {
  if (!value) return ''
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value
}

function TruncatedAxisTick(props: { x?: number; y?: number; payload?: { value: string } }) {
  const { x = 0, y = 0, payload } = props
  const fullValue = payload?.value ?? ''
  const display = truncateLabel(fullValue)
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fontSize={CHART_AXIS_FONT_SIZE} fill={CHART_AXIS_COLOR}>
        {display}
        <title>{fullValue}</title>
      </text>
    </g>
  )
}

interface DashboardPoiChartProps {
  data: DashboardStats['poisPerTenant']
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-zinc-400 gap-2">
      <BarChart2 className="h-8 w-8 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

export function DashboardPoiChart({ data }: DashboardPoiChartProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5">
      <h3 className="text-sm font-semibold text-zinc-900 mb-4">{t('dashboard.poisPerTenant')}</h3>

      {data.length === 0 ? (
        <EmptyState message={t('common.noData')} />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart
            data={data}
            margin={{ top: 8, right: 12, left: -20, bottom: 16 }}
            barCategoryGap={CHART_CATEGORY_GAP}
          >
            <XAxis
              dataKey="tenantName"
              interval={0}
              tick={<TruncatedAxisTick />}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: CHART_AXIS_FONT_SIZE, fill: CHART_AXIS_COLOR }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={48}
            />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: '#F4F4F5' }} />
            <Bar
              dataKey="count"
              fill={CHART_BAR_COLOR}
              radius={CHART_BAR_RADIUS}
              maxBarSize={CHART_BAR_MAX_SIZE}
              name={t('dashboard.poiCount', { count: '' }).trim()}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

export function DashboardPoiChartSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5">
      <Skeleton className="h-4 w-32 mb-4" />
      <Skeleton className={cn('w-full')} style={{ height: CHART_HEIGHT }} />
    </div>
  )
}
